import { intl } from '@fullstory/intl';
import { Measure, Metric } from '@fullstory/proto/fs/api/metrics';
import { TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { assertExhaustive } from '@fullstory/typeutils';

export type RollingWindow = {
  windowSize: number;
  windowType: Measure.RollingWindowType;
  windowInterval: TimeInterval;
};

export const DEFAULT_ROLLING_WINDOW: RollingWindow = {
  windowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
  windowSize: 0,
  windowInterval: TimeInterval.TIME_INTERVAL_INVALID,
} as const;

/**
 * Returns the rolling window for a metric. Due to current limitations
 * dual metrics will always have the same window size, so we don't have to check
 * each dual metric measure individually.
 *
 * @param metric
 * @returns the rolling window applied to the metric
 */
export const getRollingWindow = (metric: Metric): RollingWindow => {
  const windowSize =
    metric.single?.rollingWindow ??
    metric.dual?.first?.rollingWindow ??
    metric.dual?.second?.rollingWindow ??
    0;
  const windowType =
    metric.single?.rollingWindowType ??
    metric.dual?.first?.rollingWindowType ??
    metric.dual?.second?.rollingWindowType ??
    Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID;

  const windowInterval =
    metric.single?.rollingWindowInterval ??
    metric.dual?.first?.rollingWindowInterval ??
    metric.dual?.second?.rollingWindowInterval ??
    TimeInterval.TIME_INTERVAL_INVALID;

  return { windowSize, windowType, windowInterval };
};

export const isRollingWindowOrAverage = (metric: Metric) =>
  getRollingWindow(metric).windowType !== Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID;

/**
 * Takes the given rolling window and constructs a nicely formatted string describing it
 *
 * @param rollingWindow
 * @returns formatted string for the rolling window provided
 */
export const getFormattedRollingWindowString = ({
  windowSize,
  windowType,
  windowInterval,
}: RollingWindow) => {
  if (
    windowType === Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID ||
    windowInterval === TimeInterval.TIME_INTERVAL_INVALID ||
    windowInterval === TimeInterval.TIME_INTERVAL_ENTIRE_RANGE
  )
    return '';

  return intl.formatMessage(
    {
      defaultMessage:
        '{windowSize} {windowInterval, select, 2 {day} 1 {hour} 3 {week} other {month}} {windowType, select, 1 {rolling window} other {rolling average}}',
      description:
        'The rolling window or average size, including the time interval type of the trend. eg. 7 day rolling window',
      id: 'QnewmR',
    },
    { windowSize, windowInterval, windowType },
  );
};

/**
 * Updates a metric's rolling window
 * @param metric metric to update
 * @param rollingWindow rolling window to use for update
 * @returns a new metric with an updated rolling window
 */
export const updateRollingWindow = (metric: Metric, rollingWindow: RollingWindow): Metric => {
  if (metric.single) {
    return {
      single: {
        ...metric.single,
        rollingWindow: rollingWindow.windowSize,
        rollingWindowType: rollingWindow.windowType,
        rollingWindowInterval: rollingWindow.windowInterval,
      },
    };
  } else if (metric.dual) {
    return {
      dual: {
        ...metric.dual,
        first: metric.dual.first
          ? {
              ...metric.dual.first,
              rollingWindow: rollingWindow.windowSize,
              rollingWindowType: rollingWindow.windowType,
              rollingWindowInterval: rollingWindow.windowInterval,
            }
          : metric.dual.first,
        second: metric.dual.second
          ? {
              ...metric.dual.second,
              rollingWindow: rollingWindow.windowSize,
              rollingWindowType: rollingWindow.windowType,
              rollingWindowInterval: rollingWindow.windowInterval,
            }
          : metric.dual.second,
      },
    };
  }
  return metric;
};

const getSolrUnitFromInterval = (offset: number, interval: TimeInterval): string => {
  switch (interval) {
    case TimeInterval.TIME_INTERVAL_MINUTE:
      return `${offset}MINUTE`;
    case TimeInterval.TIME_INTERVAL_HOUR:
      return `${offset}HOUR`;
    case TimeInterval.TIME_INTERVAL_DAY:
      return `${offset}DAY`;
      break;
    case TimeInterval.TIME_INTERVAL_WEEK:
      return `${offset * 7}DAY`;
      break;
    case TimeInterval.TIME_INTERVAL_MONTH:
      return `${offset}MONTH`;
    case TimeInterval.TIME_INTERVAL_ENTIRE_RANGE:
    case TimeInterval.TIME_INTERVAL_INVALID:
      return '';
    default:
      assertExhaustive(interval, 'invalid rolling window time interval');
  }
};

export const getRollingWindowTimeRange = (
  timeRange: TimeRange,
  interval: TimeInterval,
  windowInterval: TimeInterval,
  windowSize: number,
): TimeRange => {
  const updatedRange = { ...timeRange };
  const forward = getSolrUnitFromInterval(1, interval);
  const backward = getSolrUnitFromInterval(
    windowSize,
    windowInterval === TimeInterval.TIME_INTERVAL_INVALID ? interval : windowInterval,
  );

  updatedRange.startTime += `-${backward}+${forward}`;
  return updatedRange;
};
